import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { flattenQuery } from 'helpers/flattenQuery';
import { FilterConfig } from 'hooks/useListQuery';
import { extractErrorMessage } from 'helpers/extractErrorMessage';

export interface Role {
  role_id: number;
  name: string;
  ssoroleid?: string | null;
  notification_email?: string | null;
  privileges?: any[];
  users?: { user_id: number; username: string }[];
  user_ids?: number[];
  is_protected_admin_role?: boolean;
}

interface PaginationState {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

interface RolesState {
  roles: Role[];
  pagination: PaginationState;
  status: string | null;
  error: string | null;
  config: { header_key: string; label: string; header_type: string }[];
  filtersConfig: FilterConfig[];
  loading: {
    getRolesPaginated: boolean;
  };
}

const initialState: RolesState = {
  roles: [],
  pagination: {
    totalItems: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: 2000,
  },
  status: null,
  error: null,
  config: [],
  filtersConfig: [],
  loading: {
    getRolesPaginated: false,
  },
};

export const getRolesPaginated = createAsyncThunk('roles/getRolesPaginated', async (params: Record<string, any>) => {
  const res = await api.get('/roles', { params: flattenQuery(params) });
  return res.data;
});

// Fetch a single role by ID with its privileges
export const fetchRole = createAsyncThunk('roles/fetchRole', async (role_id: string, { rejectWithValue }) => {
  try {
    const response = await api.get(`/roles/${role_id}`);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || 'Error fetching role');
  }
});

// Create a new role
export const createRole = createAsyncThunk(
  'roles/createRole',
  async (
    payload: {
      name: string;
      ssoroleid?: string;
      privileges?: any[];
      notification_email?: string;
      user_ids?: number[];
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post('/roles', payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Error creating role');
    }
  }
);

// Update a role by ID
export const updateRole = createAsyncThunk(
  'roles/updateRole',
  async (
    payload: {
      role_id: string;
      name: string;
      ssoroleid?: string;
      privileges?: any[];
      notification_email?: string;
      user_ids?: number[];
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.put(`/roles/${payload.role_id}`, payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Error updating role');
    }
  }
);

// Delete a role by ID
export const deleteRole = createAsyncThunk('roles/deleteRole', async (role_id: string, { rejectWithValue }) => {
  try {
    await api.delete(`/roles/${role_id}`);
    return { role_id };
  } catch (error: any) {
    return rejectWithValue(error.response?.data || 'Error deleting role');
  }
});

const rolesSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getRolesPaginated.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.loading.getRolesPaginated = true;
      })
      .addCase(getRolesPaginated.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const { roles, config, filtersConfig, pagination } = action.payload;

        state.roles = roles || [];
        state.config = config || [];
        state.filtersConfig = filtersConfig || [];

        const totalItems = pagination?.total_records ?? 0;
        const totalPages = Math.max(1, pagination?.total_pages ?? 1);
        const pageSize = pagination?.limit ?? state.pagination.pageSize;
        const currentPage = pagination?.current_page ?? 1;

        state.pagination.totalItems = totalItems;
        state.pagination.totalPages = totalPages;
        state.pagination.pageSize = pageSize;
        state.pagination.currentPage = currentPage > totalPages ? 1 : currentPage;

        state.loading.getRolesPaginated = false;
      })
      .addCase(getRolesPaginated.rejected, (state, action) => {
        state.status = 'failed';
        state.error = extractErrorMessage(action.payload ?? action.error.message, 'Failed to fetch roles');
        state.loading.getRolesPaginated = false;
      })

      .addCase(fetchRole.fulfilled, (state, action) => {
        const updated = action.payload as Role;
        const idx = state.roles.findIndex((r) => r.role_id === updated.role_id);
        if (idx !== -1) state.roles[idx] = updated;
      })

      .addCase(fetchRole.rejected, (state, action) => {
        state.error = extractErrorMessage(action.payload ?? action.error.message, 'Error fetching role');
      })

      .addCase(createRole.fulfilled, (state, action) => {
        state.roles.push(action.payload as Role);
      })

      .addCase(createRole.rejected, (state) => {
        state.status = 'failed';
      })

      .addCase(updateRole.fulfilled, (state, action) => {
        const updated = action.payload as Role;
        const idx = state.roles.findIndex((r) => r.role_id === updated.role_id);
        if (idx !== -1) state.roles[idx] = updated;
      })

      .addCase(updateRole.rejected, (state) => {
        state.status = 'failed';
      })

      .addCase(deleteRole.fulfilled, (state, action) => {
        const id = (action.payload as any)?.role_id;
        if (id) {
          state.roles = state.roles.filter((r) => String(r.role_id) !== String(id));
        }
      })

      .addCase(deleteRole.rejected, (state, action) => {
        state.error = extractErrorMessage(action.payload ?? action.error.message, 'Error deleting role');
      });
  },
});

export default rolesSlice.reducer;
